import { isRejectedWithValue, Middleware } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { afterPendingPasswordChange } from "@/pages/Private/redux/account/account.api";
import { tokenAdoptionDecision } from "@/utils/auth/tokenAdoption";

import { setAuth } from "@/pages/Public/redux/auth.slice";

import {
	CAMPAIGN_CONFIG_TOO_LARGE_ERROR,
	CAMPAIGN_CONFIG_TOO_LARGE_MESSAGE,
} from "@/utils/campaignConfigGuard";
import { isLifecycleError } from "@/utils/lifecycleErrors";
import { showGlobalToast } from "@/utils/showGlobalToast";

import { ROUTE_CONFIG } from "../routes/config";

const { LOGOUT, ERROR_PAGE } = ROUTE_CONFIG;

const IGNORED_ENDPOINTS = [
	"login",
	"authTwoFactorCode",
	"verifyTwoFactor",
	"disableTwoFactor",
] as const;

/** The endpoint name and query/mutation kind RTK Query stamps onto a rejected action. */
const readRejectedArg = (action: {
	meta?: unknown;
}): { endpointName?: string; type?: "query" | "mutation" } => {
	const meta = action.meta as { arg?: { endpointName?: string; type?: "query" | "mutation" } };

	if (!meta || typeof meta !== "object" || typeof meta.arg !== "object" || !meta.arg) {
		return {};
	}

	return { endpointName: meta.arg.endpointName, type: meta.arg.type };
};

export const rtkRejectQueryMiddleware: Middleware = (api) => (next) => (action) => {
	if (isRejectedWithValue(action)) {
		const payload = action?.payload as FetchBaseQueryError;
		const status = payload?.status;

		if (payload?.status === "CUSTOM_ERROR" && payload.error === CAMPAIGN_CONFIG_TOO_LARGE_ERROR) {
			showGlobalToast(CAMPAIGN_CONFIG_TOO_LARGE_MESSAGE);
		}

		const { endpointName, type } = readRejectedArg(action);

		if (status === 401) {
			if (
				endpointName &&
				!IGNORED_ENDPOINTS.includes(endpointName as (typeof IGNORED_ENDPOINTS)[number])
			) {
				const meta = action.meta as { baseQueryMeta?: { request?: Request } };
				const authorization = meta.baseQueryMeta?.request?.headers.get("authorization");
				const rejectedToken = /^Bearer\s+(\S+)$/i.exec(authorization ?? "")?.[1];

				afterPendingPasswordChange(() => {
					const storedToken = localStorage.getItem("token");
					const decision = tokenAdoptionDecision(rejectedToken, storedToken);

					if (decision === "adopt") {
						api.dispatch(setAuth({ token: storedToken }));

						return;
					}

					if (decision === "reload") {
						// Keep the old Redux bearer until the new document discards its user/forms/cache.
						window.location.reload();

						return;
					}

					// Recheck after the hard navigation: another tab may rotate in between.
					if (rejectedToken) {
						sessionStorage.setItem("rejectedAuthToken", rejectedToken);
					} else {
						sessionStorage.removeItem("rejectedAuthToken");
					}

					window.location.href = LOGOUT;
				});
			}
		} else if (status === 403 && type === "query" && !isLifecycleError(payload)) {
			// Only a *query* may take the app to the error page. A rejected mutation is
			// something the user just did, and its caller already renders the error next
			// to the control they clicked — navigating away instead reloads the whole SPA
			// and drops them somewhere else entirely, which is what PREC-3876 reported as
			// "the app crashes".
			//
			// PREC-3900 adds the second exclusion: `COMPANY_ARCHIVED` and `PLAN_INACTIVE` are
			// lifecycle *states*, not session failures. The user is signed in, their token is
			// fine, and the app already has a screen for it — `Private.tsx` renders
			// `<ArchivedAccount/>`, which names the deletion date and offers the way back. An
			// archived company's first page load fires `GET /api/usage` from `MainLayout`, which
			// the wall rejects; hard-navigating on that answer replaced the reactivate screen with
			// a bare "Forbidden" page and made the wall unreachable except by typing the billing
			// URL by hand. RTK does not abort that in-flight request when `Private` re-renders
			// into the wall, so the race is not winnable — the middleware has to stand down.
			window.location.href = ERROR_PAGE;
		}
	}

	return next(action);
};
